import React, { ReactNode, useCallback } from 'react';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { Box, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { CardGiftcard as Gift } from '@mui/icons-material';

import { LocalizationContext } from '@cvt/contexts';

import { useErrorScroll } from '@shared/hooks/useErrorScroll';

import { RecipientDetailsSection } from './RecipientDetailsSection';
import TestimonialSection from './TestimonialSection';
import PetDetailsSection from './PetDetailsSection';
import ShippingAddressSection from './ShippingAddressSection';
import MessageSection from './MessageSection';
import CreditAmountSection from './CreditAmountSection';


export type FormData = Gift.Crud;

interface Props {
  defaultValues?: Partial<FormData>;
  onSubmitRequest: (values: FormData) => void;
  isRegift?: boolean;
  slots?: {
    footer?: ReactNode | ((formState: FormState<FormData>) => ReactNode);
  }
}

const DEFAULT_VALUES: Partial<FormData> = {
  recipientName: '',
  recipientEmail: '',
  petName: '',
  shippingStreet: '',
  shippingCity: '',
  shippingState: '',
  shippingPostCode: '',
  shippingCountry: '',
  shippingChoice: 'recipient',
  message: '',
  creditAmount: 1,
};

export const GiftForm: React.FC<Props> = ({
  defaultValues = {},
  onSubmitRequest,
  isRegift,
  slots,
}) => {
  const { dictionary } = React.useContext(LocalizationContext);

  const methods = useForm<FormData>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { handleSubmit, formState, control } = methods;

  useErrorScroll(formState.errors, formState.isSubmitting);

  const onSubmit = useCallback(async (data: FormData) => {
    return onSubmitRequest(data);
  }, [onSubmitRequest]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardHeader
            sx={{ pb: 0 }}
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'secondary.main' }}>
                <Typography variant="h6" component="div">
                Gift Details
                </Typography>
              </Box>
            }
          />
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TestimonialSection />
              </Grid>

              <Grid size={12}>
                <RecipientDetailsSection
                  control={control}
                  dictionary={dictionary}
                />
              </Grid>

              {!isRegift && (
                <>
                  <Grid size={12}>
                    <PetDetailsSection
                      control={control}
                      dictionary={dictionary}
                    />
                  </Grid>

                  <Grid size={12}>
                    <CreditAmountSection
                      control={control}
                      dictionary={dictionary}
                    />
                  </Grid>

                  <Grid size={12}>
                    <ShippingAddressSection
                      control={control}
                      dictionary={dictionary}
                    />
                  </Grid>
                </>
              )}

              <Grid size={12}>
                <MessageSection
                  control={control}
                  dictionary={dictionary}
                />
              </Grid>

              <Grid size={12}>
                {typeof slots?.footer === 'function' ? slots?.footer(formState) : slots?.footer}
              </Grid>

            </Grid>
          </CardContent>
        </Card>
      </form>
    </FormProvider>
  );
};
